import { useMemo } from 'react';
import { TargetUnit } from '../../../server/utils/explorer-utils.ts';
import { useExplorerClerkTabColumns } from '../components/explorer-view-clerk.tsx';
import { useExplorerCustomerTabColumns } from '../components/explorer-view-customers.tsx';
import { useExplorerIndustryTabColumns } from '../components/explorer-view-industry.tsx';
import { useExplorerNormalisedCategoryTabColumns } from '../components/explorer-view-normalised-category.tsx';
import { useExplorerProductGroupTabColumns } from '../components/explorer-view-product-group.tsx';
import { useExplorerProductTabColumns } from '../components/explorer-view-product.tsx';
import { useExplorerSalesRepTabColumns } from '../components/explorer-view-sales-reps.tsx';
import { useExplorerStorageLocationTabColumns } from '../components/explorer-view-storage-locations.tsx';
import { useExplorerSupplierTabColumns } from '../components/explorer-view-supplier.tsx';
import {
    useExplorerColumnsWithContext,
    type ExplorerColumnsContext,
} from './use-configurable-explorer-columns.tsx';
import { useInitialExplorerState } from './use-explorer-query-params.ts';

export function useAiChatExplorerColumns(): ReturnType<typeof useExplorerColumnsWithContext> {
    const explorerState = useInitialExplorerState();
    const revenueExplorerContext: ExplorerColumnsContext = explorerState;
    const unitsExplorerContext: ExplorerColumnsContext = { ...explorerState, targetUnit: TargetUnit.UNITS };

    const clerkTabColumns = useExplorerClerkTabColumns();
    const customerTabColumns = useExplorerCustomerTabColumns();
    const industryTabColumns = useExplorerIndustryTabColumns();
    const normalisedCategoryTabColumns = useExplorerNormalisedCategoryTabColumns();
    const productGroupTabColumns = useExplorerProductGroupTabColumns();
    const productTabColumns = useExplorerProductTabColumns();
    const salesRepTabColumns = useExplorerSalesRepTabColumns();
    const storageLocationTabColumns = useExplorerStorageLocationTabColumns();
    const supplierTabColumns = useExplorerSupplierTabColumns();
    const explorerTabColumns = useMemo(
        () => ({
            columns: [
                ...clerkTabColumns.columns,
                ...customerTabColumns.columns,
                ...industryTabColumns.columns,
                ...normalisedCategoryTabColumns.columns,
                ...productGroupTabColumns.columns,
                ...productTabColumns.columns,
                ...salesRepTabColumns.columns,
                ...storageLocationTabColumns.columns,
                ...supplierTabColumns.columns,
            ],
        }),
        [
            clerkTabColumns,
            customerTabColumns,
            industryTabColumns,
            normalisedCategoryTabColumns,
            productGroupTabColumns,
            productTabColumns,
            salesRepTabColumns,
            storageLocationTabColumns,
            supplierTabColumns,
        ],
    );

    const revenueExplorerColumns = useExplorerColumnsWithContext(explorerTabColumns, revenueExplorerContext);
    const unitsExplorerColumns = useExplorerColumnsWithContext(explorerTabColumns, unitsExplorerContext);

    return useMemo(
        () => [...revenueExplorerColumns, ...unitsExplorerColumns],
        [revenueExplorerColumns, unitsExplorerColumns],
    );
}
